import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Terminal, Coins, Volume2, Bot,
  ListChecks, CalendarDays, MonitorPlay, Settings as SettingsIcon, LogOut, Trophy, Skull, Sparkles, Gift, BellRing, FolderOpen,
  Crown, Flame, Megaphone, PanelLeftClose, PanelLeftOpen, Activity, Timer,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

const items = [
  { to: "/app", label: "Overview", icon: LayoutDashboard, color: "#60A5FA", end: true },
  { to: "/app/ai", label: "AI Chatbot", icon: Bot, color: "#A78BFA" },
  { to: "/app/commands", label: "Commands", icon: Terminal, color: "#94A3B8" },
  { to: "/app/currencies", label: "Currencies", icon: Coins, color: "#F59E0B" },
  { to: "/app/interactions", label: "Interaction Builder", icon: Sparkles, color: "#C084FC" },
  { to: "/app/sounds", label: "Viewer Redeems", icon: Volume2, color: "#38BDF8" },
  { to: "/app/queue", label: "Redemption Queue", icon: ListChecks, color: "#22C55E" },
  { to: "/app/alerts", label: "Stream Alerts", icon: BellRing, color: "#F97316" },
  { to: "/app/hype", label: "Hype", icon: Flame, color: "#EF4444" },
  { to: "/app/vips", label: "VIPs", icon: Crown, color: "#FACC15" },
  { to: "/app/auto-shoutouts", label: "Auto Shoutouts", icon: Megaphone, color: "#FB7185" },
  { to: "/app/popups", label: "Popup Editor", icon: Sparkles, color: "#D946EF" },
  { to: "/app/files", label: "Files", icon: FolderOpen, color: "#A3E635" },
  { to: "/app/schedule", label: "Schedule", icon: CalendarDays, color: "#2DD4BF" },
  { to: "/app/plugs", label: "Plug Manager", icon: Megaphone, color: "#FBBF24" },
  { to: "/app/platform-events", label: "Platform Events", icon: Activity, color: "#34D399" },
  { to: "/app/giveaways", label: "Giveaways", icon: Gift, color: "#F472B6" },
  { to: "/app/rpg", label: "Barracks RPG", icon: Skull, color: "#C4B5FD" },
  { to: "/app/leaderboard", label: "Leaderboard", icon: Trophy, color: "#FDE68A" },
  { to: "/app/events", label: "Events", icon: CalendarDays, color: "#818CF8" },
  { to: "/app/obs", label: "OBS Control", icon: MonitorPlay, color: "#0A84FF" },
  { to: "/app/coworking", label: "Coworking", icon: Timer, color: "#64D2FF" },
  { to: "/app/settings", label: "Settings", icon: SettingsIcon, color: "#CBD5E1" },
];

function isRenderableIcon(Icon) {
  return typeof Icon === "function" || (Icon && typeof Icon === "object");
}

export default function Sidebar() {
  const { me, refresh } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("adminSidebarCollapsed") === "true");

  useEffect(() => {
    localStorage.setItem("adminSidebarCollapsed", collapsed ? "true" : "false");
  }, [collapsed]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    await refresh();
    navigate("/");
  };

  return (
    <aside className={`${collapsed ? "w-16" : "w-60"} shrink-0 surface flex flex-col h-screen sticky top-0 transition-[width] duration-200`} data-testid="sidebar">
      <div className={`p-4 border-b border-[hsl(var(--border))] flex items-center ${collapsed ? "justify-center" : "gap-2"}`}>
        {me?.streamer?.profile_image_url ? (
          <img src={me.streamer.profile_image_url} alt="" className="w-9 h-9 rounded border border-[hsl(var(--border))] shrink-0" />
        ) : (
          <div className="w-9 h-9 rounded bg-[hsl(var(--primary))] flex items-center justify-center text-sm font-semibold text-white shrink-0">BR</div>
        )}
        {!collapsed && (
          <div className="min-w-0">
            <div className="font-semibold tracking-tight truncate">The Barracks Rat</div>
            <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Streamer control room</div>
          </div>
        )}
      </div>

      <div className="p-2 border-b border-[hsl(var(--border))]">
        <button
          type="button"
          onClick={() => setCollapsed((value) => !value)}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className={`w-full flex items-center ${collapsed ? "justify-center" : "justify-between"} gap-2 px-3 py-2 rounded text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--surface-2))] hover:text-white`}
        >
          {!collapsed && <span>Collapse</span>}
          {collapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
        </button>
      </div>

      <nav className="p-2 flex-1 overflow-y-auto">
        {items.filter(Boolean).map((item) => {
          const { to, icon: Icon, end, color = "hsl(var(--primary))" } = item;
          const label = item.label || "Navigation item";
          const NavIcon = isRenderableIcon(Icon) ? Icon : LayoutDashboard;
          return (
            <NavLink
              key={to}
              to={to}
              end={end}
              title={collapsed ? label : undefined}
              aria-label={label}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `group flex items-center ${collapsed ? "justify-center px-2" : "gap-3 px-3"} py-2 my-0.5 rounded border-l-2 text-sm transition-colors ${
                  isActive
                    ? "bg-[hsl(var(--surface-2))] text-white"
                    : "text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--surface-2))] hover:text-white"
                }`
              }
              style={({ isActive }) => ({
                borderLeftColor: isActive ? color : "transparent",
              })}
            >
              <span
                className={`inline-flex items-center justify-center rounded shrink-0 ${collapsed ? "w-8 h-8" : "w-7 h-7"} border transition-colors`}
                style={{
                  color,
                  borderColor: `${color}44`,
                  background: `${color}14`,
                }}
              >
                <NavIcon className="w-4 h-4" strokeWidth={1.8} />
              </span>
              {!collapsed && <span>{label}</span>}
            </NavLink>
          );
        })}
      </nav>

      <div className="p-3 border-t border-[hsl(var(--border))]">
        <div className={`flex items-center ${collapsed ? "justify-center" : "gap-3"} mb-3`}>
          {me?.streamer?.profile_image_url ? (
            <img src={me.streamer.profile_image_url} alt="" className="w-9 h-9 rounded-full" />
          ) : (
            <div className="w-9 h-9 rounded-full bg-[hsl(var(--surface-2))]" />
          )}
          {!collapsed && (
            <div className="min-w-0">
              <div className="text-sm truncate">{me?.streamer?.display_name || "-"}</div>
              <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">@{me?.streamer?.twitch_login || me?.channel_login || "-"}</div>
            </div>
          )}
        </div>
        <button
          onClick={logout}
          title={collapsed ? "Logout" : undefined}
          aria-label="Logout"
          data-testid="logout-btn"
          className="w-full flex items-center justify-center gap-2 text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] py-2 hover:text-white border border-[hsl(var(--border))] rounded"
        >
          <LogOut className="w-3 h-3" /> {!collapsed && "Logout"}
        </button>
      </div>
    </aside>
  );
}
